// Session persistence for the admin area.
//
// Kept as plain module functions (not context) so `hook/services.jsx` can read
// the token when building a request without depending on React state — the
// same split smartlinks uses between `marketplace/auth` and its services hook.

const TOKEN_KEY = "mp_admin_token";
const USER_KEY = "mp_admin_user";

// localStorage throws in private-mode Safari and inside some sandboxed
// iframes, so every access is guarded.
const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const getToken = () => safe(() => localStorage.getItem(TOKEN_KEY));

export const getUser = () =>
  safe(() => {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  });

export const setSession = (token, user) =>
  safe(() => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return true;
  }, false);

export const clearSession = () =>
  safe(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    return true;
  }, false);

/**
 * When the session token stops working, from the `exp` the backend signs into
 * it (seconds since epoch). Null when it can't be read — the server's 401 is
 * then what ends the session.
 */
export const tokenExpiresAt = (token = getToken()) =>
  safe(() => {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return exp ? exp * 1000 : null;
  });

export const isTokenExpired = (token = getToken()) => {
  const at = tokenExpiresAt(token);
  return Boolean(at && Date.now() >= at);
};

/**
 * Ends an expired session: clears it and goes to the sign-in page, which says
 * why and comes back here afterwards. A full page load, so nothing from the
 * old session stays in memory.
 */
export const endSession = () => {
  clearSession();
  if (window.location.pathname.startsWith("/login")) return;
  const next = window.location.pathname + window.location.search;
  window.location.replace(`/login?expired=1&next=${encodeURIComponent(next)}`);
};
